import { Copy, Check, Download } from 'lucide-react';
import { useCallback } from 'react';
import { downloadBlob } from '@/utils/downloadFile';
import useCopyState from './useCopyState';
import CitationPillText from './CitationPillText';
import type { RespondentMap } from './useRespondentMap';

interface ArtifactMarkdownReportProps {
  title: string;
  data: { content: string };
  /**
   * Respondent → conversation lookup, computed from the thread's
   * artifacts by ArtifactPanel and passed down. Used to make `[Rn]`
   * citations inside the report content clickable. Optional for
   * back-compat with any direct callers, but always supplied by
   * ArtifactPanel in practice.
   */
  respondentMap?: RespondentMap;
}

function ArtifactMarkdownReport({ title, data, respondentMap }: ArtifactMarkdownReportProps) {
  const [copied, triggerCopied] = useCopyState();

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(data.content || '');
    triggerCopied();
  }, [data, triggerCopied]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([data.content || ''], { type: 'text/markdown' });
    downloadBlob(blob, `${title.replace(/\s+/g, '-').toLowerCase()}.md`);
  }, [data, title]);

  if (!data?.content) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
            title="Copy as plain text"
            aria-label="Copy report as plain text"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
            title="Download as .md"
            aria-label="Download report as Markdown"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-5 prose prose-sm max-w-none">
        {/* Citation pills + markdown — `CitationPillText` splits the
            content on `[Rn]` / `[Qx]` / `[Tn]` tokens, renders each
            text chunk through the same Markdown component as before,
            and interleaves clickable pills. Previously this used
            `<Markdown>{data.content}</Markdown>` which left citation
            tokens as raw text — confusing next to the clickable pills
            in the chat bubble. */}
        <CitationPillText
          text={data.content}
          respondentMap={respondentMap ?? new Map()}
          variant="paragraph"
        />
      </div>
    </div>
  );
}

export default ArtifactMarkdownReport;
